/* eslint-disable unused-imports/no-unused-vars */
import { arrayUtils, commonUtils, IconType } from '@sato/components';
import { capitalize, uniq } from 'lodash';

import {
  ApartmentDocument,
  OwnApartment,
  TvSystem,
} from '@models/apartment.types';
import { LanguageKey } from '@models/Language';
import { TranslatorFn } from '@stores/localization';
import { isLocalOrTestEnv } from '@utils/featureFlags';
import { contentfulKeysForContractTypes } from '@views/ContractPage/ContractPage.utils';

import {
  ApartmentDetailProps,
  apartmentRoomTypeTranslationsMap,
  dishwasherTranslationMap,
  featureTranslationMap,
  floorMaterialTranslationMap,
  fridgeTranslationMap,
  heatingSystemTranslationMap,
  isFormattedRoomTypeKey,
  stoveTranslationMap,
  washingMachineTranslationMap,
} from './apartment.translations';

const formatApartmentDetails = (
  {
    apartment: { fridgeType, features, washingMachine, dishwasher, stove },
    rooms,
  }: OwnApartment,
  t: TranslatorFn
): ApartmentDetailProps[] => {
  const translate = (key: string) => t(key);

  const floorMaterials = uniq(
    rooms
      ?.map(
        ({ floorMaterial }) =>
          floorMaterial && translate(floorMaterialTranslationMap[floorMaterial])
      )
      .filter(commonUtils.isTruthy)
  );

  return [
    ...features.map((feature): ApartmentDetailProps | undefined => {
      if (feature in featureTranslationMap) return;

      const { children, label } =
        featureTranslationMap[feature as keyof typeof featureTranslationMap] ??
        {};

      if (!label || !children) return;

      return {
        children: translate(children),
        label: translate(label),
      };
    }),

    dishwasher && {
      label: translate('dishwasherLabel'),
      children: translate(dishwasherTranslationMap[dishwasher]),
    },

    washingMachine && {
      label: translate('washingMachineLabel'),
      children: translate(washingMachineTranslationMap[washingMachine]),
    },

    fridgeType && {
      label: translate('fridgeTypeLabel'),
      children: translate(fridgeTranslationMap[fridgeType]),
    },

    stove && {
      label: translate('stove_type.label'),
      children: translate(stoveTranslationMap[stove]),
    },

    !!floorMaterials?.length && {
      label: translate('floorMaterialsLabel'),
      children: capitalize(floorMaterials.join(', ')),
    },
  ].filter(commonUtils.isTruthy);
};

const formatApartmentSustainabilityDetails = (
  {
    apartment: { energyClass },
    realestate: { sustainableChoice, geothermal, greenHomes },
  }: OwnApartment,
  t: TranslatorFn
): ApartmentDetailProps[] => {
  const translate = (key: string) => t(key);

  return [
    energyClass && {
      label: translate('energyClassLabel'),
      children: energyClass,
      tooltip: translate('energyClassTooltip'),
    },

    sustainableChoice && {
      label: translate('sustainableChoiceLabel'),
      children: translate('yes'),
    },

    greenHomes && {
      label: translate('greenHomesLabel'),
      children: translate('yes'),
      tooltip: translate('greenHomesTooltip'),
    },

    geothermal && {
      label: translate('heatingSystemLabel'),
      // TODO: make dynamic
      children: translate(heatingSystemTranslationMap.GEOTHERMAL),
      tooltip: translate('heatingSystemTooltip'),
    },
  ].filter(commonUtils.isTruthy);
};

const getApartmentLinkList = (
  { realestate }: OwnApartment,
  t: TranslatorFn
): Array<{ title: string; to: string }> => {
  const translate = (key: string) => t(key);

  return [
    {
      title: translate('houseRulesLinkLabel'),
      to: translate('houseRulesLink'),
    },

    realestate.pelSu && {
      title: t('safetyPlanLink'),
      to: realestate.pelSu,
    },

    {
      title: translate('responsibilitiesTableLinkLabel'),
      to: translate('responsibilitiesTableLink'),
    },

    {
      title: translate('residentBillingLinkLabel'),
      to: translate('residentBillingLink'),
    },
  ].filter(commonUtils.isTruthy);
};

type HouseRuleItem = { icon: IconType; children: string; tooltip?: string };

const formatHouseRules = (
  { realestate: { smokeFree, petsAllowed } }: OwnApartment,
  t: TranslatorFn
): HouseRuleItem[] =>
  (
    [
      !!smokeFree && {
        icon: 'noSmoking',
        children: t('noSmokingLabel'),
        tooltip: t('noSmokingTooltip'),
      },
      {
        icon: 'quietTime',
        children: t('nightTimePeaceLabel'),
        tooltip: t('nightTimePeaceTooltip'),
      },
      {
        icon: 'grill',
        children: t('grillingAllowedLabel'),
        tooltip: t('grillingAllowedTooltip'),
      },
      petsAllowed && {
        icon: 'animal',
        children: t('petsAllowedLabel'),
        tooltip: t('petsAllowedTooltip'),
      },
    ] satisfies (HouseRuleItem | boolean)[]
  ).filter(commonUtils.isTruthy);

const apartmentTranslations = {
  tvSystem: {
    CABLE: 'tv_system_cable',
    TERRESTRIAL: 'tv_system_terrestial',
    SATELLITE: 'tv_system_satellite',
  } satisfies Record<TvSystem, string>,
  contractType: contentfulKeysForContractTypes,
} as const;

export type ApartmentTranslations = typeof apartmentTranslations;

const translateApartmentAttribute = <
  AttributeT extends keyof ApartmentTranslations,
>(
  attribute: AttributeT,
  value: keyof ApartmentTranslations[AttributeT] | undefined | null,
  t: TranslatorFn
) => {
  const translationKey = value
    ? apartmentTranslations[attribute][value]
    : undefined;

  // check for string type to avoid TS errors
  if (!translationKey || typeof translationKey !== 'string') {
    if (isLocalOrTestEnv) {
      return `Translation missing for ${value?.toString()} of attribute ${attribute}`;
    }

    return '';
  }

  return t(translationKey);
};

/**
 * Translate text variants for room type information. For example for
 * room type information(from API data) '3h+alkovi',
 * @returns {
 *  label: '3h+alkovi',
 *  long: '3 huonetta + alkovi',
 *  short: '3h+alkovi',
 *  tiny: '3h+alkovi'
 * }
 */
const formatRoomTypes = (
  formattedRoomType: ApartmentDocument['formattedRoomType'] | undefined | null,
  language: LanguageKey
): string => {
  if (!formattedRoomType) return '?';

  /*
   * Split room type (e.g. 2h+kt+s+at) into segments, priotizing longest match for each info
   * segment. For example, first try to match "2h+kt+s+at" as a whole, then "2h+kt+s" (without "at").
   * If translation for "2h+kt+s" is found, return two translation keys: "2h+kt+s" and "at".
   */
  const infoSegments = formattedRoomType.replace(/\(\\W+\)/g, '').split('+');

  let start = 0;
  let end = infoSegments.length;
  const translationKeys: string[] = [];

  while (start < end) {
    const formattedRoomTypeKey = infoSegments.slice(start, end).join('+');

    if (!isFormattedRoomTypeKey(formattedRoomTypeKey)) {
      end -= 1;
      continue;
    }

    const translation = apartmentRoomTypeTranslationsMap[formattedRoomTypeKey];

    if (translation) {
      translationKeys.push(translation[language]);
      // set segment cursor to the end of newly resolved translation
      start = end;
      end = infoSegments.length;
    } else {
      end -= 1;
    }
  }

  return arrayUtils.joinArrayString(translationKeys, '+');
};

export const apartmentUtils = {
  translate: translateApartmentAttribute,
  format: {
    details: formatApartmentDetails,
    houseRules: formatHouseRules,
    linkList: getApartmentLinkList,
    sustainabilityDetails: formatApartmentSustainabilityDetails,
    roomTypes: formatRoomTypes,
  },
};
